import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { listUsers } from "../services/userService";
import { assignGeneratedContractReview } from "../services/generatedContractService";
import type { GeneratedContractDraft } from "../services/generatedContractService";
import { ApiError } from "../services/apiClient";
import type {
  Template,
  ContractReviewForm,
  SendForReviewForm,
} from "../types/template";
import type { UserOption } from "../types/user";
import { SendForReviewView } from "../components/organisms/SendForReviewView";
import { POST_COMMIT_GRAPH_MSG_KEY } from "../services/graphService";

type LocationState = {
  template: Template;
  contractReviewForm: ContractReviewForm;
  generatedContractId: string;
  /** True when opened from Select Contract, so Back returns to that list. */
  fromContractList?: boolean;
  /** State needed to return from Send for Review to the template contract review form. */
  reviewBackState?: {
    readonly draft: GeneratedContractDraft;
    readonly capturedDocumentName: string;
  };
  /** When set, poll sessionStorage for post-commit Graph upload banner messages. */
  postCommitGraphBannerPoll?: boolean;
};

export const SendForReviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [users, setUsers] = useState<UserOption[]>([]);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [graphPostCommitBanner, setGraphPostCommitBanner] = useState<
    string | null
  >(null);

  const stateOk = !!(
    state?.template &&
    state?.contractReviewForm &&
    state?.generatedContractId?.trim()
  );

  useEffect(() => {
    if (!stateOk) {
      navigate("/contractiq/templates", { replace: true });
    }
  }, [stateOk, navigate]);

  useEffect(() => {
    listUsers()
      .then(setUsers)
      .catch(() => setUsersError("Unable to load reviewers"))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!state?.postCommitGraphBannerPoll) {
      return undefined;
    }
    const read = (): void => {
      try {
        const m = sessionStorage.getItem(POST_COMMIT_GRAPH_MSG_KEY);
        if (m) {
          setGraphPostCommitBanner(m);
        }
      } catch {
        /* ignore */
      }
    };
    read();
    const id = globalThis.setInterval(read, 200);
    const stop = globalThis.setTimeout(
      () => globalThis.clearInterval(id),
      120_000,
    );
    return () => {
      globalThis.clearInterval(id);
      globalThis.clearTimeout(stop);
    };
  }, [state?.postCommitGraphBannerPoll]);

  const handleBack = () => {
    if (state?.fromContractList) {
      if (id && state.reviewBackState) {
        navigate(`/contractiq/templates/${id}/review`, {
          state: state.reviewBackState,
        });
      } else {
        navigate("/contractiq/contracts");
      }
      return;
    }
    if (id) {
      navigate(`/contractiq/templates/${id}/review`);
    } else {
      navigate("/contractiq/templates");
    }
  };

  const handleSendForReview = useCallback(
    async (form: SendForReviewForm) => {
      if (!state?.template || !state.generatedContractId?.trim()) {
        return;
      }
      setSubmitError(null);
      setIsSubmitting(true);
      try {
        await assignGeneratedContractReview(state.generatedContractId.trim(), {
          reviewer_user_id: form.reviewerId,
          review_note: form.note?.trim() ?? "",
        });
        navigate("/contractiq/templates");
      } catch (err: unknown) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unable to assign reviewer. Try again or go back.";
        setSubmitError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, state?.template, state?.generatedContractId],
  );

  if (!stateOk) {
    return null;
  }

  if (isLoading) {
    return <p className="text-muted-foreground">Loading...</p>;
  }

  return (
    <SendForReviewView
      template={state.template}
      users={users}
      usersError={usersError}
      isSubmitting={isSubmitting}
      submitError={submitError}
      graphPostCommitBanner={graphPostCommitBanner}
      onSendForReview={handleSendForReview}
      onBack={handleBack}
    />
  );
};
